import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useLoader } from '../../contexts/LoaderContext';
import { useToast } from '../../contexts/ToastContext';

// API
import { useResultadoTesteService } from '../../services/resultadoTesteServices';
import { URLS } from '../../constants/urls';

// Assets
import testePlaceHolder from '../../assets/cardBackgroundAlt.png';

// Estilos
import styles from './participantehomepage.module.css';

function ParticipanteHomePage() {
    const navigate = useNavigate();

    const { usuario } = useAuth();
    const { showLoader, hideLoader } = useLoader();
    const { showToast } = useToast();
    const { listarResultadosPorParticipante, gerarPdfDevolutiva } = useResultadoTesteService();

    const [dadosPorTurma, setDadosPorTurma] = useState({});
    const carrosselRefs = useRef({});

    const imagemDeFundo = testePlaceHolder;

    // Link do WhatsApp
    const whatsappLink = "https://wa.me/+5511973723256";

    useEffect(() => {
        const carregarResultados = async () => {
            showLoader("Carregando resultados");

            const res = await listarResultadosPorParticipante(usuario.id);

            hideLoader();
            console.log(res.resultados);
            if (res.success && res.resultados) {
                const agrupado = {};
                for (const r of res.resultados) {
                    const turmaNome = r.nomeTurma || `Turma ${r.idTurma}`;
                    if (!agrupado[turmaNome]) agrupado[turmaNome] = [];
                    agrupado[turmaNome].push(r);
                }
                setDadosPorTurma(agrupado);
            } else {
                showToast("Erro ao carregar resultados.", 3000, 'error');
            }
        };

        if (usuario?.id) carregarResultados();
    }, [usuario?.id]);

    useEffect(() => {
        Object.values(carrosselRefs.current).forEach(carrossel => {
            if (!carrossel) return;

            let isDown = false;
            let startX;
            let scrollLeft;

            const onMouseDown = (e) => {
                isDown = true;
                startX = e.pageX - carrossel.offsetLeft;
                scrollLeft = carrossel.scrollLeft;
                carrossel.classList.add(styles.activeDrag);
            };

            const onMouseLeave = () => {
                isDown = false;
                carrossel.classList.remove(styles.activeDrag);
            };

            const onMouseUp = () => {
                isDown = false;
                carrossel.classList.remove(styles.activeDrag);
            };

            const onMouseMove = (e) => {
                if (!isDown) return;
                e.preventDefault();
                const x = e.pageX - carrossel.offsetLeft;
                const walk = (x - startX) * 1.5;
                carrossel.scrollLeft = scrollLeft - walk;
            };

            carrossel.addEventListener('mousedown', onMouseDown);
            carrossel.addEventListener('mouseleave', onMouseLeave);
            carrossel.addEventListener('mouseup', onMouseUp);
            carrossel.addEventListener('mousemove', onMouseMove);

            // Cleanup
            return () => {
                carrossel.removeEventListener('mousedown', onMouseDown);
                carrossel.removeEventListener('mouseleave', onMouseLeave);
                carrossel.removeEventListener('mouseup', onMouseUp);
                carrossel.removeEventListener('mousemove', onMouseMove);
            };
        });
    }, [dadosPorTurma]);

    const handleVerRespostas = async (resultado) => {
        let deveFazerFallback = true;

        // ✅ Safari iOS: pré-abre a nova aba SINCRONAMENTE no clique.
        // Importante: NÃO usar 'noopener,noreferrer' nas features aqui, pois no Safari iOS
        // isso pode fazer você perder a referência e ficar preso em about:blank.
        const pdfWin = window.open('', '_blank');

        // Feedback básico na aba (se abriu) e desassocia o opener por segurança.
        if (pdfWin) {
            try { pdfWin.opener = null; } catch (e) {}
            try {
                pdfWin.document.title = 'Gerando PDF...';
                pdfWin.document.body.style.fontFamily = 'sans-serif';
                pdfWin.document.body.style.padding = '16px';
                pdfWin.document.body.innerHTML = '<p>Gerando seu PDF, aguarde...</p>';
            } catch (e) {}
        }

        try {
            showLoader('Gerando PDF de devolutiva...');

            const resp = await gerarPdfDevolutiva(resultado); // aceita objeto com .id

            console.log('[ParticipanteHomePage.handleVerRespostas] resp:', resp);

            if (resp?.success && resp?.relative_path) {
                const relative = resp.relative_path; // ex: "generated/resultado_....pdf"

                // Base: URLS.IMG_PATH + /reports
                const imgBase = URLS.IMG_PATH || '';
                const baseSemBarraFinal = imgBase.endsWith('/')
                    ? imgBase.slice(0, -1)
                    : imgBase;

                const reportsBase = `${baseSemBarraFinal}/reports`;

                // Garante que não tenha duas barras
                const relativeSemBarraInicial = relative.startsWith('/')
                    ? relative.slice(1)
                    : relative;

                const pdfUrl = `${reportsBase}/${relativeSemBarraInicial}`;
                // Ex: https://.../imgPath/reports/generated/arquivo.pdf

                // ✅ Redireciona a aba já aberta no clique (evita bloqueio de pop-up no Safari)
                if (pdfWin && !pdfWin.closed) {
                    try {
                        // replace evita histórico com about:blank
                        pdfWin.location.replace(pdfUrl);
                    } catch (e) {
                        // Se não conseguir redirecionar, fecha a aba extra e mantém fallback
                        try { pdfWin.close(); } catch (e2) {}
                    }

                    // ✅ Se por algum motivo ficar preso em about:blank, fecha automaticamente
                    setTimeout(() => {
                        try {
                            if (!pdfWin.closed && pdfWin.location.href === 'about:blank') {
                                pdfWin.close();
                            }
                        } catch (e) {
                            // Sem acesso => provavelmente navegou para o PDF (cross-origin). Tudo certo.
                        }
                    }, 8000);
                }

                deveFazerFallback = false;
            }
        } catch (err) {
            console.error('[ParticipanteHomePage.handleVerRespostas] erro ao gerar PDF:', err);
        } finally {
            hideLoader();
        }

        if (deveFazerFallback) {
            // Fecha a aba extra (se abriu) quando ocorrer fallback
            if (pdfWin && !pdfWin.closed) {
                try { pdfWin.close(); } catch (e) {}
            }
            navigate(`/participante/page/resultado/${resultado.id}`);
        }
    };

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.containerTop}>
                <h1 className={styles.title}>Meus Resultados</h1>
            </div>

            {Object.entries(dadosPorTurma).map(([turma, resultados], idx) => (
                <div key={turma} className={styles.turmaWrapper}>
                    <h2 className={styles.turmaTitulo}>{turma}</h2>

                    <div
                        className={styles.carrossel}
                        ref={(el) => (carrosselRefs.current[turma] = el)}
                    >
                        {resultados.map((res, i) => (
                            <div
                                key={i}
                                className={styles.card}
                                style={{
                                    backgroundImage: `url(${imagemDeFundo})`,
                                }}
                            >
                                <div className={styles.cardContent}>
                                    <h3 className={styles.cardTitulo}>{res.nomeTeste}</h3>

                                    <p className={styles.cardData}>
                                        Respondido em {new Date(res.dataEnvio).toLocaleDateString()}
                                    </p>

                                    <button
                                        className={styles.cardBotao}
                                        onClick={() => handleVerRespostas(res)}
                                    >
                                        Ver Respostas
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            
            <a
                href={whatsappLink}
                className={styles.whatsappButton}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Fale conosco no WhatsApp"
            >
                <img
                    src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                    alt="WhatsApp"
                    className={styles.whatsappIcon}
                />
            </a>
        </div>
    );
}

export default ParticipanteHomePage;
